import { Button } from "@/components/ui/button";
import { useDashboard } from "@/lib/store";
import { useToast } from "@/components/ui/use-toast";
import { ILayout } from "@/domain/interfaces/dashboard.interface";
import { v4 as uuidv4 } from "uuid";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useState } from "react";

const replaceIds = (obj: any): any => {
  if (!obj) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => replaceIds(item));
  }

  if (typeof obj === "object") {
    const newObj = { ...obj };

    // Replace id if it exists
    if ("id" in newObj) {
      newObj.id = uuidv4();
    }

    // Recursively process all object values
    for (const key in newObj) {
      if (typeof newObj[key] === "object") {
        newObj[key] = replaceIds(newObj[key]);
      }
    }

    return newObj;
  }

  return obj;
};

export function SaveLoadLayouts() {
  const { activeLayout, updateLayout, addLayout, setActiveLayoutId } =
    useDashboard((state) => ({
      activeLayout: state.layouts.find((l) => l.id === state.activeLayoutId),
      updateLayout: state.updateLayout,
      addLayout: state.addLayout,
      setActiveLayoutId: state.setActiveLayoutId,
    }));
  const { toast } = useToast();
  const [isReplaceDialogOpen, setIsReplaceDialogOpen] = useState(false);
  const [pendingLayoutData, setPendingLayoutData] = useState<ILayout | null>(
    null,
  );

  const handleSave = async () => {
    if (!activeLayout) {
      toast({
        title: "No active layout",
        description: "Please select a layout to save",
        variant: "destructive",
      });
      return;
    }

    try {
      const layoutData = JSON.stringify(activeLayout);
      await navigator.clipboard.writeText(layoutData);
      toast({
        title: "Layout saved",
        description: "Layout has been copied to clipboard",
      });
    } catch (error) {
      toast({
        title: "Error saving layout",
        description: "Failed to copy layout to clipboard",
        variant: "destructive",
      });
    }
  };

  const handleLoad = async () => {
    if (!activeLayout) {
      toast({
        title: "No active layout",
        description: "Please select a layout to replace",
        variant: "destructive",
      });
      return;
    }

    try {
      const clipboardText = await navigator.clipboard.readText();
      const layoutData = JSON.parse(clipboardText) as ILayout;

      // Validate layout data
      if (!layoutData.json || !layoutData.name) {
        throw new Error("Invalid layout data");
      }

      // Store the layout data and open confirmation dialog
      setPendingLayoutData(layoutData);
      setIsReplaceDialogOpen(true);
    } catch (error) {
      console.error(error);
      toast({
        title: "Error loading layout",
        description: "Invalid layout data in clipboard",
        variant: "destructive",
      });
    }
  };

  const handleConfirmReplace = () => {
    if (!activeLayout || !pendingLayoutData) {
      return;
    }

    // Replace all IDs in the layout content
    const updatedLayout = replaceIds(pendingLayoutData);
    updatedLayout.id = activeLayout.id;

    console.log(updatedLayout);
    // Update the active layout with new content
    updateLayout(updatedLayout);

    setIsReplaceDialogOpen(false);
    setPendingLayoutData(null);

    toast({
      title: "Layout loaded",
      description: `Current layout has been updated with imported content`,
    });
  };

  const handleCancelReplace = () => {
    setIsReplaceDialogOpen(false);
    setPendingLayoutData(null);
  };

  return (
    <>
      <div className="flex gap-2 pr-2">
        <Button
          variant="outline"
          size="xs"
          onClick={handleSave}
          disabled={!activeLayout}
        >
          Save to Clipboard
        </Button>
        <Button variant="outline" size="xs" onClick={handleLoad}>
          Load from Clipboard
        </Button>
      </div>

      <Dialog open={isReplaceDialogOpen} onOpenChange={setIsReplaceDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Replace layout content</DialogTitle>
            <DialogDescription>
              Are you sure you want to replace the content of{" "}
              <span className="underline">{activeLayout?.name}</span> with{" "}
              <span className="underline">{pendingLayoutData?.name}</span>? This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="destructive" onClick={handleConfirmReplace}>
              Yes, replace it
            </Button>
            <Button variant="secondary" onClick={handleCancelReplace}>
              No, keep it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
